import { type GetOrganizationsQuery } from "../../shared/api/queries";
import { type WebLensFolder, type WebLensOrganization } from "../model";

const byName = <T extends { name: string }>(a: T, b: T): number => a.name.localeCompare(b.name);

/**
 * Reshapes the shared getOrganizations payload for the picker: folders deduped by id and both
 * levels sorted alphabetically, matching the lens-submission flow's option semantics
 * (makeLensSpaceOptions) so the same creator sees the same folder list in both flows.
 */
export const mapWebLensOrganizations = (data: GetOrganizationsQuery | undefined): WebLensOrganization[] =>
    (data?.getMyLensesOrganizations.organizationsList ?? [])
        .map((organization) => {
            const foldersById = new Map<string, WebLensFolder>(
                organization.lensSpacesList.map((lensSpace) => [
                    lensSpace.id,
                    { id: lensSpace.id, name: lensSpace.name },
                ]),
            );
            return {
                id: organization.id,
                name: organization.name,
                folders: [...foldersById.values()].sort(byName),
            };
        })
        .sort(byName);

export interface EffectiveFolderSelection {
    organization: WebLensOrganization | null;
    folder: WebLensFolder | null;
}

const soleItem = <T>(items: readonly T[]): T | null => (items.length === 1 ? items[0] ?? null : null);

/**
 * Resolves the route's selection against the loaded organizations. The org comes from the
 * `:orgId` path segment (container-feature convention) and is matched strictly — a foreign or
 * stale org id yields null and the page shows its picker rather than guessing. The folder's
 * sole-option fallback applies ONLY when nothing was requested: a ?lens-folder= id that doesn't
 * resolve within the org (stale bookmark, deleted folder, another org's folder) yields null so
 * the flow re-asks, never silently substituting a destination the URL didn't name. Deriving at
 * render time (rather than writing fallbacks back to the URL from an effect) keeps the URL the
 * single source of intent.
 */
export const resolveEffectiveSelection = (
    organizations: readonly WebLensOrganization[],
    pathOrganizationId: string,
    requestedFolderId: string,
): EffectiveFolderSelection => {
    const organization = organizations.find((candidate) => candidate.id === pathOrganizationId) ?? null;
    if (!organization) {
        return { organization: null, folder: null };
    }

    const folder = requestedFolderId
        ? organization.folders.find((candidate) => candidate.id === requestedFolderId) ?? null
        : soleItem(organization.folders);
    return { organization, folder };
};
